import {
  addDays,
  getDay,
  getDaysInMonth,
  getMonth,
  getYear,
  isAfter,
  isBefore,
  isSameDay,
  isValid,
  parse,
  startOfToday,
} from 'date-fns';

type DisabledDatesFunction = (date: Date, options: { minDate: Date; maxDate: Date }) => boolean;

interface GetParsedDateParams {
  date: string | Date | undefined;
  dateFormat: string;
}

/**
 * Returns the parsed date that can be processed using date-fns library.
 *
 * @param {GetParsedDateParams} params - The parameters object.
 * @param {string | Date | undefined} params.date - The date to be parsed, can be a Date instance or string.
 * @param {string} params.dateFormat - The format of the given date.
 * @returns {Date} The parsed date.
 */
export const getParsedDate = ({ date, dateFormat }: GetParsedDateParams) => {
  if (typeof date === 'string' && isValid(new Date(date))) {
    return parse(date, dateFormat, startOfToday());
  }
  return date as Date;
};

/**
 * Returns a valid year number clamped between min and max year bounds.
 *
 * @param {Date} date - The date that provides the year to be compared.
 * @param {number} minYear - The minimum year.
 * @param {number} maxYear - The maximum year.
 * @returns {number} The clamped year value.
 */
export const getValidYear = (date: Date, minYear: number, maxYear: number): number =>
  Math.min(Math.max(getYear(date), minYear), maxYear);

/**
 * Returns a 2D array representing calendar days for the given month and year.
 * Each week is represented as an array of 7 elements (day numbers or null for adjacent months).
 *
 * @param {number} targetMonth - The target month (0-11).
 * @param {number} targetYear - The target year.
 * @returns {Array<Array<number | null>>} 6x7 matrix of day numbers and nulls.
 */
export const getMonthMatrix = (
  targetMonth: number,
  targetYear: number,
): Array<Array<number | null>> => {
  const daysInMonth = getDaysInMonth(new Date(targetYear, targetMonth));
  const firstDayOfWeek = getDay(new Date(targetYear, targetMonth));
  const monthMatrix: Array<Array<number | null>> = [[]];

  const MAX_ROWS = 6;

  // push null if day(s) belong to the previous month
  for (let i = 0; i < firstDayOfWeek; i += 1) {
    monthMatrix[0].push(null);
  }

  // push days to the matrix by month
  for (let i = 0; i < daysInMonth; i += 1) {
    if (monthMatrix[monthMatrix.length - 1].length === 7) {
      monthMatrix.push([]);
    }

    monthMatrix[monthMatrix.length - 1].push(i + 1);
  }

  // push days if day(s) belong to the next month
  for (let i = monthMatrix[monthMatrix.length - 1].length; i < 7; i += 1) {
    monthMatrix[monthMatrix.length - 1].push(null);
  }

  // fill in padding rows if needed
  if (monthMatrix.length < MAX_ROWS) {
    for (let i = monthMatrix.length; i < MAX_ROWS; i += 1) {
      monthMatrix.push([null, null, null, null, null, null, null]);
    }
  }

  return monthMatrix;
};

/**
 * Checks if a date falls between the min and max date range (inclusive).
 *
 * @param {Date} newDate - The date to be compared.
 * @param {Date} minDate - The minimum date in the range.
 * @param {Date} maxDate - The maximum date in the range.
 * @returns {boolean} True if the date is within the range.
 */
export const isDateInBetween = (newDate: Date, minDate: Date, maxDate: Date): boolean =>
  (isAfter(newDate, minDate) || isSameDay(newDate, minDate)) &&
  (isBefore(newDate, maxDate) || isSameDay(newDate, maxDate));

/**
 * Checks if a date is disabled based on disabled dates array/function and date range.
 *
 * @param {Date} date - The date to be checked.
 * @param {Date[] | DisabledDatesFunction} disabledDates - Array of disabled dates or function to check if date is disabled.
 * @param {Date} minDate - The minimum allowed date.
 * @param {Date} maxDate - The maximum allowed date.
 * @returns {boolean} True if the date is disabled.
 */
export const isDisabledDate = (
  date: Date,
  disabledDates: Array<Date> | DisabledDatesFunction,
  minDate: Date,
  maxDate: Date,
): boolean => {
  if (typeof disabledDates === 'function') {
    return disabledDates(date, { minDate, maxDate });
  }

  return (
    isBefore(date, minDate) ||
    isAfter(date, maxDate) ||
    disabledDates.some((value) => isSameDay(date, value))
  );
};

/**
 * Returns the first available (non-disabled) date in the given date's month.
 *
 * @param {Date | undefined} givenDate - The date to start searching from.
 * @param {Date[] | DisabledDatesFunction} disabledDates - Array of disabled dates or function to check if date is disabled.
 * @param {Date} minDate - The minimum allowed date.
 * @param {Date} maxDate - The maximum allowed date.
 * @returns {Date} The first available date in the month.
 */
export const getFirstAvailableDate = (
  givenDate: Date | undefined,
  disabledDates: Array<Date> | DisabledDatesFunction,
  minDate: Date,
  maxDate: Date,
): Date => {
  // If the date given is invalid, use the current date
  let date = givenDate ?? startOfToday();
  if (!isValid(date)) {
    date = startOfToday();
  }

  if (!isDisabledDate(date, disabledDates, minDate, maxDate)) {
    return date;
  }

  // Iterate over days until we find a non disabled days, or we reach the max date
  const year = getYear(date);
  const month = getMonth(date);
  date = new Date(year, month, 1);
  while (isDisabledDate(date, disabledDates, minDate, maxDate) && date.getMonth() === month) {
    date = addDays(date, 1);
  }

  // Check again if valid (we might have hit end of month or all invalid dates)
  if (!isDisabledDate(date, disabledDates, minDate, maxDate) && date.getMonth() === month) {
    return date;
  }

  // Return the first day of the month
  return new Date(year, month, 1);
};

/**
 * Finds the next available (non-disabled) day after the given date.
 *
 * @param {Date} givenDate - The starting date.
 * @param {Date[] | DisabledDatesFunction} disabledDates - Array of disabled dates or function to check if date is disabled.
 * @param {Date} minDate - The minimum allowed date.
 * @param {Date} maxDate - The maximum allowed date.
 * @returns {Date | null} The next available date or null if none found.
 */
export const getNextAvailableDay = (
  givenDate: Date,
  disabledDates: Array<Date> | DisabledDatesFunction,
  minDate: Date,
  maxDate: Date,
): Date | null => {
  let date = givenDate;
  do {
    date = addDays(date, 1);
  } while (isDisabledDate(date, disabledDates, minDate, maxDate) && isBefore(date, maxDate));

  // Check again if valid (we might have hit all invalid dates)
  if (!isDisabledDate(date, disabledDates, minDate, maxDate)) {
    return date;
  }
  return null;
};

/**
 * Finds the previous available (non-disabled) day before the given date.
 *
 * @param {Date} givenDate - The starting date.
 * @param {Date[] | DisabledDatesFunction} disabledDates - Array of disabled dates or function to check if date is disabled.
 * @param {Date} minDate - The minimum allowed date.
 * @param {Date} maxDate - The maximum allowed date.
 * @returns {Date | null} The previous available date or null if none found.
 */
export const getPreviousAvailableDay = (
  givenDate: Date,
  disabledDates: Array<Date> | DisabledDatesFunction,
  minDate: Date,
  maxDate: Date,
): Date | null => {
  let date = givenDate;
  do {
    date = addDays(date, -1);
  } while (isDisabledDate(date, disabledDates, minDate, maxDate) && isAfter(date, minDate));

  // Check again if valid (we might have hit all invalid dates)
  if (!isDisabledDate(date, disabledDates, minDate, maxDate)) {
    return date;
  }
  return null;
};
